import { useCallback, cloneElement, useMemo } from "react";
import type { EmblaCarouselType, EmblaOptionsType } from "embla-carousel";
import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { ChevronLeft, ChevronRight, Circle } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { usePrevNextButtons } from "@/components/Carousel/CarouselArrowButtons";
import { useDotButton } from "@/components/Carousel/CarouselDotButton";
import { v4 as uuidv4 } from "uuid";
import Tag from "../Tags/Tag";
import {
  useResponsiveGroupSize,
  groupSections,
} from "./useResponsiveGroupSize";
import CTALink from "@/components/CTA/variants/CtaLink";
import { getEmblaSlideAccessibilityProps } from "@/components/Carousel/emblaSlideAccessibility";
import { useCarouselMessages } from "@/i18n/hooks/useCarouselMessages";
import useTranslations from "@/i18n/useTranslations";
import "./styles.css";

type NewProductCarouselProps = {
  sections: React.ReactNode[];
  pageTitle?: string;
  subheader?: string;
  useAutoplay?: boolean;
  autoplayDelay?: number;
  backgroundColor?: string;
  cta?: { label: string; url: string; showCta: boolean };
};

const options: EmblaOptionsType = {
  loop: true,
  slidesToScroll: 1,
  align: "start",
};

export default function NewProductCarousel({
  sections,
  pageTitle = "New Products",
  subheader = "",
  useAutoplay = false,
  autoplayDelay = 1,
  backgroundColor = "putty-100",
  cta,
}: NewProductCarouselProps) {
  const tCommon = useTranslations("Common");
  const carouselMessages = useCarouselMessages();

  const groupSize = useResponsiveGroupSize();
  const groupedSections = useMemo(
    () => groupSections(sections, groupSize),
    [sections, groupSize],
  );
  const showControls = groupedSections.length > 1;

  const plugins = useAutoplay
    ? [Autoplay({ delay: autoplayDelay * 1000 })]
    : []; // convert seconds to milliseconds
  const [emblaRef, emblaApi] = useEmblaCarousel(options, plugins);

  const onNavButtonClick = useCallback((emblaApi: EmblaCarouselType) => {
    const autoplay = emblaApi?.plugins()?.autoplay;
    if (!autoplay) return;
    autoplay.stop();
  }, []);

  const { selectedIndex, scrollSnaps, onDotButtonClick } = useDotButton(
    emblaApi,
    onNavButtonClick,
  );

  const {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  } = usePrevNextButtons(emblaApi, onNavButtonClick);

  const handleSectionFocus = useCallback(() => {
    const autoplay = emblaApi?.plugins()?.autoplay;
    if (autoplay) {
      autoplay.stop();
    }
  }, [emblaApi]);

  const handleSectionBlur = useCallback(() => {
    const autoplay = emblaApi?.plugins()?.autoplay;
    if (autoplay) {
      autoplay.play();
    }
  }, [emblaApi]);

  const chevronIconProps = { width: 24, height: 24 };

  return (
    <div
      className={`${backgroundColor ? `bg-${backgroundColor}` : ""} py-16 fullwidth`}
    >
      <div
        className={`new-product-carousel embla flex flex-col space-y-10 bg-${backgroundColor}`}
      >
        <div className="flex flex-col items-center">
          <div className="w-full flex flex-col space-y-4 md:space-y-0 md:flex-row md:space-x-3  justify-center items-center text-center">
            <Tag
              title={tCommon("newBadge")}
              color="orange"
              className="h-7 px-2 py-2 text-sm font-bold leading-none [border-radius:4px]"
            />
            <h1 className="elm-d1 italic text-primary-500-light p-0">
              {pageTitle}
            </h1>
          </div>
          {subheader && (
            <div className="text-[18px] leading-[25px] mt-3 font-normal text-gray-800-light text-center">
              {subheader}
            </div>
          )}
        </div>

        <div className="flex">
          {showControls && (
            <div className="hidden md:flex items-center pr-3">
              <Button
                variant="link"
                size="icon"
                onClick={onPrevButtonClick}
                className="text-primary hover:text-brand"
                disabled={prevBtnDisabled}
              >
                <ChevronLeft svgProps={chevronIconProps} />
                <span className="sr-only">
                  {carouselMessages.goToPreviousSlide}
                </span>
              </Button>
            </div>
          )}
          {/* Slides */}
          <div
            // this z-index should be higher than the one @src/components/PartnerCarousel/PartnerCarousel.tsx
            // and lower than elements that go above:
            // the sitewide banner @src/components/Navigation/DesktopNav/index.tsx
            // and the userMenu @src/components/Navigation/UserMenu/index.tsx
            className="embla__viewport z-10"
            ref={emblaRef}
            role="region"
            aria-label={carouselMessages.homepageHeroCarousel}
            aria-roledescription="carousel"
          >
            <div className="embla__container flex">
              {groupedSections.map((group, slideIndex) => {
                const isVisible = slideIndex === selectedIndex;

                return (
                  <div
                    key={slideIndex}
                    className="embla__slide"
                    aria-label={`Slide ${slideIndex + 1}`}
                    aria-roledescription="slide"
                    {...getEmblaSlideAccessibilityProps(isVisible)}
                  >
                    <div className="embla__slide-grid">
                      {group.map((section, sectionIndex) => {
                        const clonedSection = cloneElement(
                          section as React.ReactElement<any>,
                          {
                            onFocus: handleSectionFocus,
                            onBlur: handleSectionBlur,
                          },
                        );
                        return (
                          <div key={sectionIndex} className="embla__slide-item">
                            {clonedSection}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          {showControls && (
            <div className="hidden md:flex items-center pl-3">
              <Button
                variant="link"
                size="icon"
                onClick={onNextButtonClick}
                className="text-primary hover:text-brand"
                disabled={nextBtnDisabled}
              >
                <ChevronRight svgProps={chevronIconProps} />
                <span className="sr-only">
                  {carouselMessages.goToNextSlide}
                </span>
              </Button>
            </div>
          )}
        </div>

        {/* Controls */}
        <div className="w-full bottom-0 px-12 pb-0 pointer-events-none">
          {showControls && (
            <div className="w-full flex justify-center">
              <div className="flex items-center bg-white gap-2 rounded-full pointer-events-auto h-[40px]">
                <Button
                  variant="link"
                  size="icon"
                  onClick={onPrevButtonClick}
                  className="text-primary hover:text-brand"
                  disabled={prevBtnDisabled}
                >
                  <ChevronLeft svgProps={{ width: 32, height: 32 }} />
                  <span className="sr-only">
                    {carouselMessages.goToPreviousSlide}
                  </span>
                </Button>
                {scrollSnaps.map((_, index) => (
                  <Button
                    key={uuidv4()}
                    variant="link"
                    size="icon"
                    onClick={() => onDotButtonClick(index)}
                    className={`hover:text-brand ${index === selectedIndex ? "text-brand" : "text-primary"}`}
                  >
                    <Circle svgProps={{ width: 14, height: 14 }} />
                    <span className="sr-only">
                      {carouselMessages.goToSlide(index + 1)}
                    </span>
                  </Button>
                ))}
                <Button
                  variant="link"
                  size="icon"
                  onClick={onNextButtonClick}
                  className="text-primary hover:text-brand"
                  disabled={nextBtnDisabled}
                >
                  <ChevronRight svgProps={{ width: 32, height: 32 }} />
                  <span className="sr-only">
                    {carouselMessages.goToNextSlide}
                  </span>
                </Button>
              </div>
            </div>
          )}
          {cta && cta.showCta && (
            <div className="w-full mt-6 md:mt-10 flex justify-center pointer-events-auto">
              <CTALink
                label={cta.label}
                url={cta.url}
                type="brand"
                className="px-5 py-1 text-base text-white font-normal"
                html_id="gtm-spotlight-carousel-cta"
                location="gtm-spotlight-carousel"
                name="browse-new-courses"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
